/**
 * Banner — strata-style callout strip.
 *
 * Implements design-system/Components.html § 14 (Callouts).
 *
 * Four canonical tones: `note` · `success` · `warn` · `fail`.
 * Aliases kept for backwards compatibility:
 *   - `ice` → `note`
 *   - `ok` → `success`
 *   - `neutral` → `note`
 *
 * Variants:
 *   - Short (default): padding 10/14, radius 8, icon 14, gap 10, font 13/1.5.
 *   - Long-form: pass `title` to switch to 14/16 padding, top-aligned icon,
 *     mono uppercase eyebrow above the body, gap 12.
 *
 * Light mode: surface-aware via CSS variables (set in tokens.css under
 * `.surface-light`). No JS branching required.
 */
import type { CSSProperties, ReactNode } from 'react';
import { Info, AlertTriangle, SrCheck } from './icons-lib';

export type BannerTone =
  | 'note'
  | 'success'
  | 'warn'
  | 'fail'
  // Backwards-compat aliases (existing call sites).
  | 'ice'
  | 'ok'
  | 'neutral';

interface BannerProps {
  tone?: BannerTone;
  /** Long-form heading. Switches the layout to top-aligned + mono eyebrow.
   *  Accepts a string (rendered as a mono-uppercase eyebrow) or a ReactNode
   *  for callers that need an inline action / link beside the heading. */
  title?: ReactNode;
  children?: ReactNode;
  /** Override the default icon. By default each tone ships with its own. */
  icon?: ReactNode;
  /** Hide the icon entirely. */
  hideIcon?: boolean;
  action?: ReactNode;
  onDismiss?: () => void;
  style?: CSSProperties;
}

type CanonicalTone = 'note' | 'success' | 'warn' | 'fail';

const TONE_ALIAS: Record<BannerTone, CanonicalTone> = {
  note: 'note',
  success: 'success',
  warn: 'warn',
  fail: 'fail',
  ice: 'note',
  ok: 'success',
  neutral: 'note',
};

// Per-tone palette. Dark values are the defaults from Components.html.
// Light values live in tokens.css `.surface-light`. The component reads
// from CSS custom properties so a parent `.surface-light` swaps both
// surfaces and callouts at once without JS branching.
const TONE_VARS: Record<CanonicalTone, { bgVar: string; brVar: string; fgVar: string; iconVar: string }> = {
  note: {
    bgVar: '--callout-note-bg',
    brVar: '--callout-note-br',
    fgVar: '--callout-note-fg',
    iconVar: '--callout-note-icon',
  },
  success: {
    bgVar: '--callout-success-bg',
    brVar: '--callout-success-br',
    fgVar: '--callout-success-fg',
    iconVar: '--callout-success-icon',
  },
  warn: {
    bgVar: '--callout-warn-bg',
    brVar: '--callout-warn-br',
    fgVar: '--callout-warn-fg',
    iconVar: '--callout-warn-icon',
  },
  fail: {
    bgVar: '--callout-fail-bg',
    brVar: '--callout-fail-br',
    fgVar: '--callout-fail-fg',
    iconVar: '--callout-fail-icon',
  },
};

// Eyebrow color is one step muted from body fg — separate var for that nudge.
const EYEBROW_VAR: Record<CanonicalTone, string> = {
  note: '--callout-note-eyebrow',
  success: '--callout-success-eyebrow',
  warn: '--callout-warn-eyebrow',
  fail: '--callout-fail-eyebrow',
};

function defaultIcon(tone: CanonicalTone): ReactNode {
  const sz = 14;
  switch (tone) {
    case 'success':
      return <SrCheck size={sz} strokeWidth={1.75} />;
    case 'warn':
      return <AlertTriangle size={sz} strokeWidth={1.75} />;
    case 'fail':
      return (
        <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" strokeWidth={1.75} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" />
          <path d="M9 9l6 6M15 9l-6 6" />
        </svg>
      );
    case 'note':
    default:
      return <Info size={sz} strokeWidth={1.75} />;
  }
}

export default function Banner({
  tone = 'note',
  title,
  children,
  icon,
  hideIcon = false,
  action,
  onDismiss,
  style,
}: BannerProps) {
  const canonical = TONE_ALIAS[tone];
  const vars = TONE_VARS[canonical];
  const isLong = Boolean(title);
  const renderedIcon = icon ?? defaultIcon(canonical);

  return (
    <div
      role="status"
      data-callout={canonical}
      style={{
        display: 'flex',
        alignItems: isLong ? 'flex-start' : 'center',
        gap: isLong ? 12 : 10,
        padding: isLong ? '14px 16px' : '10px 14px',
        background: `var(${vars.bgVar})`,
        color: `var(${vars.fgVar})`,
        border: `1px solid var(${vars.brVar})`,
        borderRadius: 8,
        fontSize: 13,
        lineHeight: isLong ? 1.55 : 1.5,
        ...style,
      }}
    >
      {!hideIcon && (
        <span
          aria-hidden="true"
          style={{
            color: `var(${vars.iconVar})`,
            flexShrink: 0,
            display: 'inline-flex',
            marginTop: isLong ? 2 : 0,
          }}
        >
          {renderedIcon}
        </span>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        {title && (
          <div
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: `var(${EYEBROW_VAR[canonical]})`,
              marginBottom: 4,
            }}
          >
            {title}
          </div>
        )}
        {children && <div>{children}</div>}
      </div>
      {action && <div style={{ flexShrink: 0, marginLeft: 8 }}>{action}</div>}
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          style={{
            flexShrink: 0,
            width: 22,
            height: 22,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: `var(${vars.iconVar})`,
            background: 'transparent',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
            opacity: 0.7,
            transition: 'opacity 120ms ease',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.7')}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>
      )}
    </div>
  );
}
